import {
  type FC,
  type JSX,
  useState,
  useEffect,
  useRef,
  lazy,
  Suspense,
  useMemo,
} from 'react';

import {
  AppHeader,
  checkEmpty,
  clearStore,
  compareArrays,
  deleteStoreValue,
  getStoreValue,
  Icon,
  setStoreValue,
  Tooltip,
} from 'pixel-react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPlatformTabsData } from 'service/commonUtils';

import {
  CLIENT_HEADER_LEFT_CONTENT,
  CLIENT_HEADER_RIGHT_CONTENT,
  helpHeaderItems,
  CLIENT_HEADER_APP_SWITCH,
} from '@/constants/headerConstants';
import { CONFIG } from '@/lib/constants/config';
import { getPrivilegeStateData } from '@/lib/utils/commonUtils';
import { notifyCurrentProjectChange } from '@/lib/utils/currentProject';
import { type RootState } from '@/store';
import { useAppSelector } from '@/store/hooks';
import { useGetMockPrivilegesQuery } from '@/store/privilege/privilegeApi';
import { useLazyGetAllResultConfigQuery } from '@/store/privilegeAI/privilegeai';
import { Data } from '@/types/login';

import styles from './Header.module.scss';
import {
  getProjectTypeIconNames,
  withHeaderMenuIcons,
} from './headerMenuIcons';
import type {
  clientHeaderContent,
  CurrentProjectProps,
  headerMenuProps,
  headerQuickMenuProps,
  headerSubMenuProps,
  projectOptionProps,
  RepoFirstNodeType,
  SuiteFirstNodeType,
} from './types';
import Profile from '../userProfile/Profile';

const ProjectListRemote = lazy(() => import('projects/sharedComponents'));
export const Header: FC = (): JSX.Element => {
  const privilegeTypeData = useAppSelector(
    (state: RootState) => state.privilege,
  );
  const privilegeContent = getPrivilegeStateData(privilegeTypeData);

  const details = import.meta.env.VITE_CLIENT_DETAIL;
  const location = useLocation();
  const navigate = useNavigate();
  const { refetch: getPrivilegeData } = useGetMockPrivilegesQuery();
  const queryParams = new URLSearchParams(location.search);
  const scriptId = queryParams.get('scriptId');
  const elementType = queryParams.get('elementType') === 'project-elements';
  const platformToDelete = queryParams.get('platformType');
  const [allProjectData, setAllProjectData] = useState([]);

  const deleteRepoFirstNode = () => {
    const isStepGroup = location.pathname.endsWith('step-group');
    const isprogramElement = location.pathname.endsWith('program-elements');
    const repoData =
      (getStoreValue('repoFirstNode') as RepoFirstNodeType) || {};
    if (platformToDelete && elementType) {
      if (repoData?.platformNodes?.[platformToDelete]) {
        delete repoData.platformNodes[platformToDelete];
        setStoreValue('repoFirstNode', repoData);
      }
    } else if (isprogramElement) {
      if (repoData && 'peLandingFirstNode' in repoData) {
        delete repoData.peLandingFirstNode;
      }
      setStoreValue('repoFirstNode', repoData);
    } else if (isStepGroup) {
      if (repoData && 'sgLandingFirstNode' in repoData) {
        delete repoData.sgLandingFirstNode;
      }
      setStoreValue('repoFirstNode', repoData);
    }
  };

  const deleteSuitesFirstNode = () => {
    const path = window.location.pathname;
    const words = ['variables-set', 'data-set', 'email-groups', 'labels'];
    const hasMatch = words.some(word => path.includes(word));
    const isSuitesLp = location.pathname.endsWith('suites');
    const isScheduleLp = location.pathname.endsWith('scheduled-executions');
    const isRunSettings = location.pathname.endsWith('runsettings');
    const isIndividalSuitesLp =
      location.pathname.includes('suite-details') &&
      !isRunSettings &&
      !hasMatch;
    const isCreateEditSuite =
      location.pathname.endsWith('automation') ||
      location.pathname.endsWith('manual');
    const isCreateEditSchedule = location.pathname.endsWith('create-schedule');
    const suiteFirstNode =
      (getStoreValue('suiteFirstNode') as SuiteFirstNodeType) || {};
    const setStore = (suiteData: SuiteFirstNodeType) => {
      setStoreValue('suiteFirstNode', suiteData);
    };
    if (isSuitesLp) {
      setStore({
        scheduleTreeFirstNode: suiteFirstNode.scheduleTreeFirstNode,
      });
    } else if (isIndividalSuitesLp) {
      setStore({
        ...suiteFirstNode,
        individualSuiteTreeFirstNode: '',
        moduleTreeFirstNode: '',
      });
    } else if (isScheduleLp) {
      setStore({
        suiteTreeFirstNode: suiteFirstNode.suiteTreeFirstNode,
      });
    } else if (isRunSettings || isCreateEditSchedule || isCreateEditSuite) {
      setStore({
        ...suiteFirstNode,
        moduleTreeFirstNode: '',
      });
    }
  };

  const userDetails = getStoreValue(CONFIG.AUTH.USER_DETAILS) as {
    responseObject: { expires_in: string; licenseStatus: string };
  };
  const isHelpmodule = location.pathname.split('/')[2] === 'help';
  const { expires_in } = userDetails?.responseObject ?? '';

  const isHeaderDisabled = Number(expires_in) < 0 || checkEmpty(expires_in);
  const projectList = allProjectData?.map(
    (item: { project: projectOptionProps }) => item?.project,
  );
  const currentProject = getStoreValue(
    'current-project',
  ) as CurrentProjectProps;

  const [triggerGetAIPrivilegeData] = useLazyGetAllResultConfigQuery();
  const getaiAPi = async () => {
    if (currentProject?.id !== 'All Projects') {
      const { data } = await triggerGetAIPrivilegeData();
      setStoreValue('aiPrivilegeDetails', data?.responseObject as Data);
    }
  };

  useEffect(() => {
    getaiAPi();
  }, [currentProject?.id]);
  const privilegeStateData = useSelector((state: RootState) => state.privilege);

  const privilegeData = useMemo(() => {
    return getPrivilegeStateData(privilegeStateData);
  }, [privilegeStateData]);
  const [headerMenuItems, setHeaderMenuItems] = useState<headerMenuProps[]>([]);
  const [selectedMenuItem, setSelectedMenuItem] =
    useState<headerMenuProps | null>(null);
  const [selectedSubMenu, setSelectedSubMenu] =
    useState<headerSubMenuProps | null>(null);

  const [selectedMenuLabel, setSelectedMenuLabel] =
    useState<string>('All Projects');
  const [selectedSubMenuLabel, setSelectedSubMenuLabel] = useState<string>('');
  const [selectedQuickMenuLabel, setSelectedQuickMenuLabel] =
    useState<string>('');
  const [selectedProjectDetails, setSelectedProjectDetails] = useState({
    label: 'All Projects',
    value: 'All Projects',
    iconName: 'all_projects',
  });
  const [projectsArray, setProjectsArray] = useState<projectOptionProps[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const appendSlash = (path: string) => {
    return '/' + path;
  };
  // Inside a project, Integrations opens that project's integrations (from the nav bar and the More menu alike)
  const menuUrl = (label: string, path: string) => {
    const project = getStoreValue('current-project') as
      { id: string } | undefined;
    return label === 'Integrations' && project && project.id !== 'All Projects'
      ? `/app${path}/${project.id}`
      : `/app${path}`;
  };
  const onMenuClickHandler = (menuItem: headerMenuProps) => {
    setSelectedMenuLabel(menuItem.label);
    setSelectedMenuItem(menuItem);
    if (isHelpmodule) {
      navigate('/app/help' + menuItem?.path);
    }
    const selectedMenu = headerMenuItems?.find(
      route => route.label === menuItem.label,
    );
    if (selectedMenu) navigate(menuUrl(selectedMenu.label, selectedMenu.path));
  };
  const onSubMenuClickHandler = (menuItem: headerSubMenuProps) => {
    setSelectedSubMenuLabel(menuItem.label);
    setSelectedSubMenu(menuItem);

    if (selectedMenuItem?.subMenuItems) {
      const selectedSubMenu = selectedMenuItem.subMenuItems?.find(
        (route: { label: string }) => route.label === menuItem.label,
      );

      if (selectedSubMenu) navigate('/app' + selectedSubMenu?.path);
    }
  };

  const onQuickMenuClickHandler = (menuItem: headerQuickMenuProps) => {
    setSelectedQuickMenuLabel(menuItem.path);

    if (selectedSubMenu?.quickMenuItems) {
      const selectedQuickMenu = selectedSubMenu.quickMenuItems?.find(
        (route: { label: string }) => route.label === menuItem.label,
      );
      if (selectedQuickMenu) {
        const { pathname, search } = location;
        const currentURLItems = pathname.split('/');
        const updatedPathItems = appendSlash(
          currentURLItems.slice(2).join('/'),
        ).split('/');

        const lastPathItem = currentURLItems[currentURLItems.length - 1];
        const isLastSegmentValid = selectedSubMenu.quickMenuItems.some(
          (item: { path: string }) => item.path === lastPathItem,
        );

        let quickMenuURL = '';
        // If the last path item is quick-menu it'll be sliced and new one will be added, if not new will be added directly to URL
        if (isLastSegmentValid && menuItem.path !== lastPathItem) {
          quickMenuURL = `${updatedPathItems.slice(0, -1).join('/')}/${menuItem.path}`;
          navigate('/app' + `${quickMenuURL}${search}`);
        } else if (!isLastSegmentValid) {
          if (
            menuItem.path === 'pre-post-conditions' ||
            menuItem.path === 'step-group'
          ) {
            quickMenuURL = `${updatedPathItems.join('/')}/${menuItem.path}`;
            const isScriptLevelQuickAccess = !!new URLSearchParams(search).get(
              'scriptId',
            );
            const updatedSearch = isScriptLevelQuickAccess
              ? `${search}&prevUrl=${encodeURIComponent(location.pathname + location.search)}`
              : search;
            navigate('/app' + quickMenuURL + updatedSearch);
            return;
          }
          quickMenuURL = `${updatedPathItems.join('/')}/${menuItem.path}`;
          navigate('/app' + `${quickMenuURL}${search}`);
        }
      }
    }
  };

  const onProjectsClickHandler = async (project: projectOptionProps) => {
    setStoreValue('current-project', {
      id: project.value,
      type: project.type,
      name: project.label,
      platform: project.platform,
      appType: project.appType,
      defectPlatform: project?.defectPlatform,
      status: project.status,
      timeZoneName: project.timeZoneName,
      timeZoneValue: project.timeZoneValue,
      timeZoneOffset: project.timeZoneOffset,
      createdOn: project.createdOn,
    });
    notifyCurrentProjectChange();
    deleteStoreValue('testDataLandingFirstNode');
    deleteStoreValue('testDevLandingFirstNode');
    deleteStoreValue('wsLandingFirstNode');
    deleteStoreValue('suiteFirstNode');
    const { defaultTab } = getPlatformTabsData(project?.type, privilegeContent);
    setStoreValue('previousPlatformType', defaultTab);
    setStoreValue('repoFirstNode', {});
    const prevData = await getPrivilegeData().unwrap();
    setSelectedProjectDetails(project);
    if (project.label === ('All Projects' as string)) {
      navigate('/app/projects');
      setSelectedMenuLabel('All Projects');
    } else {
      const headerData =
        prevData?.responseObject?.projectLevels?.individualProjects;
      const hasTestDev = headerData?.headers?.some(
        (header: { label: string }) => header.label === 'Test Dev',
      );
      if (hasTestDev) {
        navigate('/app/test-dev/scripts');
      } else {
        navigate('/app/projects/project');
        setSelectedMenuLabel('All Projects');
      }
    }
  };

  useEffect(() => {
    if (location.pathname === '/app/projects') {
      const allProject = {
        id: 'All Projects',
        name: 'All Projects',
      };

      setStoreValue('current-project', allProject);
      notifyCurrentProjectChange();
      setSelectedProjectDetails({
        label: 'All Projects',
        value: 'All Projects',
        iconName: 'all_projects',
      });

      setSelectedMenuLabel('All Projects');
    }
  }, [location.pathname]);

  const onProjectLabelClickHandler = () => {
    if (currentProject.id === 'All Projects') {
      navigate('/app/projects');
    } else {
      navigate('/app/projects/project');
    }
    setSelectedMenuLabel('All Projects');
  };

  const getPath = (path: string, index: number) => {
    const routePathItems = path.split('/');
    return appendSlash(routePathItems[index]);
  };
  const getCurrentRoute = (headerItems: headerMenuProps[]) => {
    const pathItems = window.location.pathname.split('/');
    // Find selected menu
    const selectedMenu = headerItems?.find(
      route => getPath(route.path, 1) === appendSlash(pathItems[2]),
    );
    if (selectedMenu) {
      setSelectedMenuLabel(selectedMenu?.label);
      setSelectedMenuItem(selectedMenu);

      // Find selected submenu, if subMenuItems exist
      const selectedSubMenu = selectedMenu?.subMenuItems?.find(
        (route: { path: string }) =>
          getPath(route.path, 2) === appendSlash(pathItems[3]),
      );

      if (selectedSubMenu) {
        setSelectedSubMenuLabel(selectedSubMenu?.label);
        setSelectedSubMenu(selectedSubMenu);
        const currentURLItems = location.pathname.split('/');
        // Find selected quick menu, if quickMenuItem exist
        const selectedQuickMenuItem = currentURLItems
          .filter(item => item)
          .map(path =>
            selectedSubMenu?.quickMenuItems?.find(
              (obj: { path: string }) => obj?.path === path,
            ),
          )
          .filter(Boolean);

        if (selectedQuickMenuItem && selectedQuickMenuItem[0]) {
          setSelectedQuickMenuLabel(selectedQuickMenuItem[0]?.path);
        } else {
          setSelectedQuickMenuLabel('');
        }
      }
    }
  };
  const setAppHeaderItems = () => {
    const token = getStoreValue(CONFIG.AUTH.TOKEN_KEY);
    const headersData = withHeaderMenuIcons(privilegeData?.headers || []);
    if (token) {
      setHeaderMenuItems(headersData);
      getCurrentRoute(headersData);
    } else {
      clearStore();
      navigate('/');
    }
  };

  useEffect(() => {
    if (currentProject?.name) {
      const projectDetails = {
        appType: currentProject.appType,
        iconName: getProjectTypeIconNames(currentProject?.type),
        label: currentProject?.name,
        platform: currentProject.platform,
        status: currentProject.status,
        timeZoneName: currentProject.timeZoneName,
        timeZoneOffset: currentProject.timeZoneOffset,
        timeZoneValue: currentProject.timeZoneValue,
        value: currentProject?.id,
        type: currentProject.type,
        defectPlatform: currentProject?.defectPlatform,
        createdOn: currentProject?.createdOn,
      };
      setSelectedProjectDetails(projectDetails);
    }
  }, [currentProject?.name]);

  const onMoreOptionClick = (
    option: { label: string; value: string },
    calculateVisibleItems: () => void,
  ) => {
    if (option) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        calculateVisibleItems();
      }, 800);
      navigate(menuUrl(option.label, option.value));
    }
  };
  useEffect(() => {
    if (!privilegeData?.headers) return;
    setAppHeaderItems();
  }, [location, privilegeData?.headers]);

  const updateProjectsAndSetStoreValue = (
    allProjects: projectOptionProps[],
    currentProjectId: string | undefined,
    projectsArray: projectOptionProps[],
    setProjectsArray: (projects: projectOptionProps[]) => void,
    setStoreValue: (key: string, value: any) => void,
  ) => {
    const updatedProjectsArray: projectOptionProps[] = [
      {
        label: 'All Projects',
        value: 'All Projects',
        iconName: 'all_projects',
        type: '',
      },
    ];
    allProjects?.forEach((project: projectOptionProps) => {
      if (project.status !== 'Archived') {
        updatedProjectsArray.push({
          label: project.name ?? '',
          value: project.id ?? '',
          iconName: getProjectTypeIconNames(project.type),
          type: project.type,
          platform: project.platform ?? '',
          appType: project.appType ?? '',
          status: project.status ?? '',
          defectPlatform: project?.defectPlatform ?? {},
          timeZoneName: project.timeZoneName ?? '',
          timeZoneValue: project.timeZoneValue ?? '',
          timeZoneOffset: project.timeZoneOffset ?? '',
          createdOn: project?.createdOn,
        });
      }
    });

    if (
      checkEmpty(projectsArray) ||
      !compareArrays(updatedProjectsArray, projectsArray)
    ) {
      setProjectsArray(updatedProjectsArray);
    }

    const matchedProject = updatedProjectsArray.find(
      project => project.value === currentProjectId,
    );

    if (
      matchedProject &&
      location.pathname !== '/app/projects' &&
      currentProjectId !== 'All Projects'
    ) {
      setStoreValue('current-project', {
        id: matchedProject.value,
        type: matchedProject.type,
        name: matchedProject.label,
        platform: matchedProject.platform,
        appType: matchedProject.appType,
        defectPlatform: matchedProject.defectPlatform,
        status: matchedProject.status,
        timeZoneName: matchedProject.timeZoneName,
        timeZoneValue: matchedProject.timeZoneValue,
        timeZoneOffset: matchedProject.timeZoneOffset,
        createdOn: matchedProject?.createdOn,
      });
    }
  };
  useEffect(() => {
    if (projectList) {
      updateProjectsAndSetStoreValue(
        projectList,
        currentProject?.id,
        projectsArray,
        setProjectsArray,
        setStoreValue,
      );
    }
  }, [projectList, currentProject?.id]);

  useEffect(() => {
    const projectId = currentProject?.id;

    getPrivilegeData();
    if (projectId === 'All Projects') {
      setStoreValue('repoFirstNode', {});
    }
  }, [currentProject?.id]);

  useEffect(() => {
    const matchedItem = helpHeaderItems.find(item =>
      location.pathname.startsWith(`/app/help${item.path}`),
    );
    if (matchedItem) {
      setSelectedMenuItem(matchedItem);
      setSelectedMenuLabel(matchedItem.label);
    }
  }, [location.pathname]);

  const isClientTrue = navigator.userAgent.includes('fire-flink-client');
  const isMac = navigator.userAgent.includes('Macintosh');

  const clearFirstNodeDataInLocalStorage = () => {
    if (!queryParams.get('scriptId')) {
      deleteStoreValue('testDevLandingFirstNode');
    }
    if (!queryParams.get('fileId')) {
      deleteStoreValue('testDataLandingFirstNode');
    }
    deleteStoreValue('wsLandingFirstNode');
    deleteStoreValue('testDevExecutionFirstNode');
    deleteRepoFirstNode();
    deleteSuitesFirstNode();
  };

  useEffect(() => {
    const navEntries = performance.getEntriesByType('navigation');
    const navigationEntry = navEntries[0] as
      PerformanceNavigationTiming | undefined;
    const isReload = ['reload', 'navigate'].includes(
      navigationEntry?.type || '',
    );
    if (isReload) {
      clearFirstNodeDataInLocalStorage();
    }
  }, []);

  const renderContent = (
    content: clientHeaderContent[],
    wrapperStyle: string,
  ): JSX.Element => (
    <div className={wrapperStyle}>
      {content?.map((item: clientHeaderContent, index: number) => (
        <Tooltip key={index} title={item.title}>
          <span id={item.name}>
            <Icon color="white" name={item.name} variant="dark" hoverEffect />
          </span>
        </Tooltip>
      ))}
    </div>
  );

  const rightContent =
    isClientTrue &&
    (isMac
      ? renderContent(CLIENT_HEADER_APP_SWITCH, styles.right_content_wrapper)
      : renderContent(
          CLIENT_HEADER_RIGHT_CONTENT,
          styles.right_content_wrapper,
        ));

  const leftContent = isClientTrue
    ? renderContent(CLIENT_HEADER_LEFT_CONTENT, styles.left_content_wrapper)
    : null;
  const GetLogo = () => {
    return (
      <>
        {details?.name === 'FireFlink' ? (
          <Icon
            color=""
            name={details?.logoUrl}
            width={details?.logoUrl === 'fireflink_icon' ? 21 : 62}
            height={24}
          />
        ) : (
          <img src={details?.logoUrl} alt="logo" width={62} height={24} />
        )}
      </>
    );
  };

  return (
    <div>
      <AppHeader
        logo={<GetLogo />}
        profileContent={!isClientTrue && <Profile />}
        rightContent={rightContent}
        leftContent={leftContent}
        appHeaderMenuItems={isHelpmodule ? helpHeaderItems : headerMenuItems}
        projectsList={isHelpmodule ? [] : projectsArray}
        selectedMenu={selectedMenuLabel}
        selectedSubMenu={selectedSubMenuLabel}
        selectedQuickMenu={selectedQuickMenuLabel}
        selectedProject={selectedProjectDetails}
        onMenuClick={onMenuClickHandler}
        onSubMenuClick={onSubMenuClickHandler}
        onQuickMenuClick={onQuickMenuClickHandler}
        onProjectMenuClick={onProjectsClickHandler}
        onProjectDropdownLabelClick={onProjectLabelClickHandler}
        onMoreMenuOptionClick={onMoreOptionClick}
        width={isClientTrue ? '-webkit-fill-available' : '1120px'}
        borderRadius="8px"
        disabled={isHeaderDisabled}
        isClient={isClientTrue}
        scriptId={scriptId}
      />
      <Suspense>
        <ProjectListRemote
          onData={(data: any) => {
            if (!checkEmpty(data)) {
              setAllProjectData(data);
            }
          }}
        />
      </Suspense>
    </div>
  );
};
